import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import GetFormDetailsInteractor from "../../interactor/form/GetFormDetailsInteractor";
import { IReferenceValue } from "../../model/form/ReferenceValue";
import SendFormVersionInteractor from "../../interactor/form/SendFormVersionInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import {
  StatusForm,
  Errors,
  AttributeFormConstants
} from "../../common/Constants";
import GetFormVersionsInteractor from "../../interactor/form/GetFormVersionsInteractor";
import AddRowInteractor from "../../interactor/form/AddRowInteractor";
import DeleteRowInteractor from "../../interactor/form/DeleteRowInteractor";
import DeleteDraftVersionFormInteractor from "../../interactor/form/DeleteDraftVersionFormInteractor";
import AddNewDraftFormVersionInteractor from "../../interactor/form/AddNewDraftFormVersionInteractor";
import ChangeFormAttributeInteractor from "../../interactor/form/ChangeFormAttributeInteractor";
import GetReferenceValueListInteractor from "../../interactor/form/GetReferenceValueListInteractor";
import ShowInfoNotificationBoxInteractor from "../../interactor/notifications/ShowInfoNotificationBoxInteractor";
import { IFormQualifierAttribute } from "../../model/form/FormQualifierAttribute";
import FormValidator from "../../common/utils/FormValidator";
import { IFormInputValueValidationError } from "../../model/validation/FormInputValueValidationError";
import { IFormVersion } from "../../model/form/FormVersion";
import FormSectionPresentationModel, {
  IFormSectionPresentationModel
} from "./model/FormSectionPresentationModel";
import ValidateFormInteractor from "../../interactor/form/ValidateFormInteractor";
import { IFormSectionQualifierPresentationModel } from "./model/FormSectionQualifierPresentationModel";
import { IFormSection } from "../../model/form/FormSection";
import { IReport1Applicant } from "../../model/report/Report1Applicant";
import { IFormVersionItem } from "../../model/report/FormVersionItem";
import GetFormReportInteractor from "../../interactor/form/GetFormReportInteractor";
import UploadSignedReportInteractor from "../../interactor/form/UploadSignedReportInteractor";
import { IFormTypeItem } from "../../model/report/FormTypeItem";
import { IReportingPeriodFormType } from "../../model/reporting/ReportingPeriodFormType";
import GetReportingPeriodFormTypesInteractor from "../../interactor/reporting/GetReportingPeriodFormTypesInteractor";

export interface TFormDetailsPresenter extends TLoadingAwarePresenter {
  formVersionList: IFormVersion[];
  formSectionList?: IFormSectionPresentationModel[];
  selectedVersion?: IFormVersion;
  referenceValueList?: IReferenceValue[];
  isEditableForm: boolean;
  currentVersionId?: number;
  deletedRowQualifierId?: number;
  reportingPeriodApplicantId: number;
  reportingPeriodFormType?: IReportingPeriodFormType;
  isPreviousForm?: boolean;
  applicantId: number;
  validationErrors: IFormInputValueValidationError[];
  firstValidatedAttribute?: IFormInputValueValidationError;
  sendFormModalVisible?: boolean;
  reportingPeriodId?: number;
  formQualifierValidationErrors?: number[];
  hasRightOfEntry?: boolean;
  comparisonVersionsModalVisible?: boolean;
  compareVersionsSelected?: number[];
  firstComparisonFormVersion: IFormSectionPresentationModel[];
  secondComparisonFormVersion: IFormSectionPresentationModel[];
  comparisonFormVersionsVisible: boolean;
  pdfReportData?: IFormSectionPresentationModel[];
  applicant?: IReport1Applicant;
  formVersion?: IFormVersionItem;
  pdfReportVisbile: boolean;
  formType?: IFormTypeItem;
  reportingPeriodFormTypeId: number;
  allowedFileTypes: string;
  isSignedReportRequired: boolean;
  signedReportValidationError: boolean;
}
export interface IFormDetailsPresenter
  extends TFormDetailsPresenter, TPresentable {
  loadFormVersionList(
    formVersionList: IFormVersion[],
    versionId?: number,
    isChangedFormVersion?: boolean
  ): void;
  loadReportingPeriodId(reportingPeriodId?: number): void;
  loadAllowedFileTypes(fileTypes?: string): void;
  loadIsSignedReportRequired(value: boolean): void;
  resetSignedReportValidationError(): void;
  setReportingPeriodFormType(
    reportingPeriodFormType?: IReportingPeriodFormType
  ): void;
  onSelectVersion(
    selectedVersionId: IFormVersion,
    isShownVersion: boolean
  ): void;
  getReferenceValues(
    attributeCode: string,
    formQualifierId: number,
    formTypeCode: string,
    qualifierCode: string,
    formAttributeId: number
  ): void;
  isDraftFormVersion(): boolean;
  onChangeFormVersion(): void;
  onCancel(): void;
  changeFormAttribute(
    formAttributeId: number,
    blob: any,
    valueClob: string,
    valueDate: Date,
    valueNumber: number,
    valueReferenceId: any,
    valueReference: string,
    valueString: string,
    formQualifierId: number | undefined,
    mimeType: string
  ): void;
  addRow(formSectionId: number): void;
  sendFormVersion(signedDocument?: File): Promise<boolean>;
  deleteRow(qualifierId: number): void;
  showSendFormVersionDialog(): Promise<void>;
  hideSendFormVersionDialog(): void;
  setRightOfEntry(value: boolean): void;
  deleteDraftFormVersion(formVersionId: number): void;
  onChangeComapreVersionsModal(visible: boolean): void;
  onSelectCompareVersions(formVersions: number[]): void;
  getComparisonFormVersionsDetails(): void;
  onChangeComparisonFormVersionsVisible(visible: boolean): void;
  generatePdfReport(): void;
  loadFormType(formType: IFormTypeItem): void;
}

const defaultState: TFormDetailsPresenter = {
  formVersionList: [],
  isEditableForm: false,
  reportingPeriodApplicantId: 0,
  applicantId: 0,
  validationErrors: [],
  firstValidatedAttribute: {} as IFormInputValueValidationError,
  sendFormModalVisible: false,
  formQualifierValidationErrors: [],
  hasRightOfEntry: false,
  comparisonVersionsModalVisible: false,
  compareVersionsSelected: [],
  firstComparisonFormVersion: [],
  secondComparisonFormVersion: [],
  comparisonFormVersionsVisible: false,
  pdfReportData: undefined,
  pdfReportVisbile: false,
  reportingPeriodFormTypeId: 0,
  allowedFileTypes: "",
  isSignedReportRequired: true,
  signedReportValidationError: false
};

const FormDetailsPresenter = withStore(
  ({ application, store, loader, translate }): IFormDetailsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TFormDetailsPresenter>();

    loader.start("formSectionLoader");

    const loadFormVersionList = async (
      formVersionList: IFormVersion[],
      versionId?: number,
      isChangedFormVersion?: boolean
    ) => {
      _store.update({
        formVersionList
      });
      const selectedVersion =
        _store.getState<TFormDetailsPresenter>().selectedVersion;
      if (versionId) {
        const formVersion = formVersionList.find(
          (item) => item.version === versionId
        );
        formVersion &&
          (await onSelectVersion(formVersion, isChangedFormVersion || false));
      } else {
        const lastFormVersion = formVersionList[formVersionList.length - 1];
        const formVersion = isChangedFormVersion
          ? lastFormVersion
          : selectedVersion;

        await onSelectVersion(
          formVersion || lastFormVersion,
          isChangedFormVersion || false
        );
      }

      loader.stop("formSectionLoader");
    };

    const loadFormType = (formType: IFormTypeItem) => {
      _store.update({ formType });
    };

    const loadReportingPeriodId = (reportingPeriodId: number) => {
      _store.update({
        reportingPeriodId
      });
    };
    const loadAllowedFileTypes = (fileTypes?: string) => {
      _store.update({
        allowedFileTypes: fileTypes
      });
    };

    const loadIsSignedReportRequired = (isSignedReportRequired: boolean) => {
      _store.update({ isSignedReportRequired });
    };

    const resetSignedReportValidationError = () => {
      _store.update({ signedReportValidationError: false });
    };

    const setReportingPeriodFormType = (
      reportingPeriodFormType?: IReportingPeriodFormType
    ) => {
      _store.update({
        reportingPeriodFormType
      });
    };

    const getReportingPeriodFormType = async () => {
      const { reportingPeriodApplicantId, reportingPeriodFormTypeId } =
        _store.getState<IFormDetailsPresenter>();
      const reportingPeriodFormTypes = await _application.container
        .resolve<GetReportingPeriodFormTypesInteractor>(
          "getReportingPeriodFormTypes"
        )
        .execute(reportingPeriodApplicantId);

      const reportingPeriodFormType = reportingPeriodFormTypes.find(
        (reportingPeriodFormType) =>
          reportingPeriodFormType.id === reportingPeriodFormTypeId
      );

      _store.update({ reportingPeriodFormType });
    };

    const getFormVersionList = async (isChangedFormVersion?: boolean) => {
      try {
        const reportingPeriodFormType =
          _store.getState<TFormDetailsPresenter>().reportingPeriodFormType;
        const formVersionList =
          reportingPeriodFormType?.formReportingPeriodId &&
          (await _application.container
            .resolve<GetFormVersionsInteractor>("getFormVersions")
            .execute(reportingPeriodFormType.formReportingPeriodId));
        formVersionList &&
          (await loadFormVersionList(
            formVersionList,
            undefined,
            isChangedFormVersion
          ));
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getFormSections = async (versionId: number) => {
      try {
        let formSectionList = (
          await _application.container
            .resolve<GetFormDetailsInteractor>("getFormDetails")
            .execute(versionId)
        ).map(FormSectionPresentationModel);
        _store.update({
          formSectionList,
          currentVersionId: versionId
        });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const pdfReportDataPresentationModelMapper = (
      formSections: IFormSection[]
    ) => {
      return formSections.map((formSection: IFormSection) => {
        return FormSectionPresentationModel(formSection);
      });
    };

    const getPDFReportData = async (formVersionId: number) => {
      try {
        loader.start("pdfReportLoader");

        const languageCode = 0;

        const { applicant, formVersion, formSections } =
          await _application.container
            .resolve<GetFormReportInteractor>("getFormReport")
            .execute(formVersionId, languageCode);

        _store.update({
          applicant,
          formVersion,
          pdfReportData: pdfReportDataPresentationModelMapper(formSections)
        });

        loader.stop("pdfReportLoader");
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const onSelectVersion = async (
      selectedVersion: IFormVersion,
      isShownVersion: boolean
    ) => {
      if (isShownVersion) {
        const { reportingPeriodFormType } =
          _store.getState<TFormDetailsPresenter>();
        reportingPeriodFormType &&
          application.navigator.replace({
            state: {
              reportingPeriodApplicantId:
                _store.getState<TFormDetailsPresenter>()
                  .reportingPeriodApplicantId,
              reportingPeriodFormTypeId: reportingPeriodFormType.id,
              formReportingPeriodId:
                reportingPeriodFormType.formReportingPeriodId,
              versionId: selectedVersion?.version
            },
            search: selectedVersion && `version=${selectedVersion.version}`
          });
      }
      if (selectedVersion) {
        await getFormSections(selectedVersion.id);
        _store.update({ selectedVersion, pdfReportVisbile: false });
      } else {
        _store.update({ formSectionList: [], currentVersionId: undefined });
        _store.update({
          selectedVersion,
          isEditableForm: false,
          pdfReportVisbile: false
        });
      }
    };

    const getReferenceValues = async (
      attributeCode: string,
      formQualifierId: number,
      formTypeCode: string,
      qualifierCode: string,
      formAttributeId: number
    ) => {
      try {
        const applicantId =
          _store.getState<TFormDetailsPresenter>().applicantId;
        const reportingPeriodId =
          _store.getState<TFormDetailsPresenter>().reportingPeriodId;
        const referenceValueList = await _application.container
          .resolve<GetReferenceValueListInteractor>("getReferenceValueList")
          .execute(
            applicantId,
            attributeCode,
            formQualifierId,
            formTypeCode,
            qualifierCode,
            formAttributeId,
            reportingPeriodId
          );
        _store.update({
          referenceValueList
        });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const isDraftFormVersion = () => {
      const formVersionList =
        _store.getState<TFormDetailsPresenter>().formVersionList;
      let isDraftVersion = false;
      formVersionList.forEach((version) => {
        if (version.status === StatusForm.DRAFT) {
          isDraftVersion = true;
        }
        return version;
      });
      return isDraftVersion;
    };

    const onChangeFormVersion = async () => {
      const selectedVersion =
        _store.getState<TFormDetailsPresenter>().selectedVersion;
      const reportingPeriodApplicantId =
        _store.getState<TFormDetailsPresenter>().reportingPeriodApplicantId;
      const reportingPeriodFormTypeId =
        _store.getState<TFormDetailsPresenter>().reportingPeriodFormTypeId;
      if (selectedVersion) {
        if (selectedVersion.isDraftVersion()) {
          _store.update({
            isEditableForm: true
          });
          application.container
            .resolve<ShowInfoNotificationBoxInteractor>(
              "showInfoNotificationBox"
            )
            .execute(
              translate("form_details.notification_box.title"),
              translate("form_details.notification_box.description")
            );
        } else {
          loader.start("onChangeFormLoader");
          try {
            if (isDraftFormVersion()) {
              const formVersionList =
                _store.getState<TFormDetailsPresenter>().formVersionList;
              const draftVersionId = formVersionList.find((item) =>
                item.isDraftVersion()
              )?.id;
              draftVersionId &&
                (await _application.container
                  .resolve<DeleteDraftVersionFormInteractor>(
                    "deleteDraftVersionForm"
                  )
                  .execute(draftVersionId));
            }
            reportingPeriodFormTypeId &&
              (await _application.container
                .resolve<AddNewDraftFormVersionInteractor>(
                  "addNewDraftFormVersion"
                )
                .execute(
                  reportingPeriodApplicantId,
                  reportingPeriodFormTypeId,
                  selectedVersion.id
                ));
            await getReportingPeriodFormType();
            await getFormVersionList(true);
            loader.stop("onChangeFormLoader");
          } catch (error) {
            loader.stop("onChangeFormLoader");
            _application.container
              .resolve<ShowErrorMessageInteractor>("showErrorMessage")
              .execute((error as any).message);
          }
        }
      } else {
        loader.start("onChangeFormLoader");
        try {
          reportingPeriodFormTypeId &&
            (await _application.container
              .resolve<AddNewDraftFormVersionInteractor>(
                "addNewDraftFormVersion"
              )
              .execute(reportingPeriodApplicantId, reportingPeriodFormTypeId));

          await getReportingPeriodFormType();
          await getFormVersionList(true);
          loader.stop("onChangeFormLoader");
        } catch (error) {
          loader.stop("onChangeFormLoader");
          _application.container
            .resolve<ShowErrorMessageInteractor>("showErrorMessage")
            .execute((error as any).message);
        }
      }
    };

    const onCancel = () => {
      _store.update({
        isEditableForm: false
      });
    };

    const changeFormAttribute = async (
      formAttributeId: number,
      blob: any,
      valueClob: string,
      valueDate: Date,
      valueNumber: number,
      valueReferenceId: any,
      valueReference: string,
      valueString: string,
      formQualifierId: number,
      mimeType: string
    ) => {
      try {
        loader.start("changeFormAttributeLoader");
        const { selectedVersion } = _store.getState<TFormDetailsPresenter>();

        let { validationErrors, formQualifierValidationErrors } =
          _store.getState<TFormDetailsPresenter>();

        if (
          formQualifierId &&
          formQualifierValidationErrors?.includes(formQualifierId)
        ) {
          formQualifierValidationErrors = formQualifierValidationErrors.filter(
            (value) => formQualifierId !== value
          );
        }

        let newValidationErrors = validationErrors.filter(
          (validationError: IFormInputValueValidationError) =>
            validationError.formAttributeId !== formAttributeId
        );

        _store.update({
          validationErrors: newValidationErrors ? newValidationErrors : [],
          formQualifierValidationErrors
        });

        if (valueReferenceId === AttributeFormConstants.REF_2_DEFAULT_ID) {
          valueReferenceId = undefined;
        }

        if (selectedVersion) {
          await _application.container
            .resolve<ChangeFormAttributeInteractor>("changeFormAttribute")
            .execute(
              formAttributeId,
              blob,
              valueClob,
              valueDate,
              valueNumber,
              valueReferenceId,
              valueReference,
              valueString,
              mimeType
            );
          await getFormSections(selectedVersion.id);
        }
        loader.stop("changeFormAttributeLoader");
      } catch (error) {
        loader.stop("changeFormAttributeLoader");
        if (error.code === Errors.DB_ERROR_CODE) {
          const { validationErrors } = _store.getState<any>();
          validationErrors.push({
            formAttributeId,
            message: (error as any).message.trim()
          });
          _store.update({ validationErrors });
        } else {
          _application.container
            .resolve<ShowErrorMessageInteractor>("showErrorMessage")
            .execute((error as any).message);
        }
      }
    };

    const addRow = async (formSectionId: number) => {
      try {
        const { currentVersionId } = _store.getState<any>();
        const selectedVersion =
          _store.getState<TFormDetailsPresenter>().selectedVersion;

        selectedVersion &&
          (await _application.container
            .resolve<AddRowInteractor>("addRow")
            .execute(selectedVersion.id, formSectionId));

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("form_details.row_add_success_message"));

        await getFormSections(currentVersionId);
      } catch (error) {
        loader.stop("formSectionLoader");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const deleteRow = async (qualifierId: number) => {
      try {
        const { currentVersionId } = _store.getState<any>();

        await _application.container
          .resolve<DeleteRowInteractor>("deleteRow")
          .execute(qualifierId);

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("form_details.row_delete_success_message"));

        await getFormSections(currentVersionId);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const validateAttributeValues = (
      section: IFormSectionPresentationModel
    ): IFormInputValueValidationError[] => {
      return FormValidator().validateAttributeValues(section);
    };

    const validateForm = (
      formSections: IFormSectionPresentationModel[] = []
    ) => {
      let validationErrors: IFormInputValueValidationError[] = [];
      formSections.forEach((formSection: IFormSectionPresentationModel) => {
        if (
          formSection.subsections !== null &&
          formSection.subsections !== undefined
        ) {
          formSection.subsections.forEach((section) => {
            section.qualifiers?.forEach(
              (qualifier: IFormSectionQualifierPresentationModel) => {
                qualifier.attributes?.forEach(
                  (attribute: IFormQualifierAttribute) => {
                    validationErrors = validationErrors.concat(
                      validateAttributeValues(section)
                    );
                  }
                );
              }
            );
          });
        }
        formSection.qualifiers?.forEach(
          (qualifier: IFormSectionQualifierPresentationModel) => {
            qualifier.attributes?.forEach(
              (attribute: IFormQualifierAttribute) => {
                validationErrors = validationErrors.concat(
                  validateAttributeValues(formSection)
                );
              }
            );
          }
        );
      });
      return validationErrors;
    };

    const getFormVersions = async (formReportingPeriodId: number) => {
      let formVersions = await _application.container
        .resolve<GetFormVersionsInteractor>("getFormVersions")
        .execute(formReportingPeriodId);

      loadFormVersionList(formVersions, undefined, true);
    };

    const sendFormVersion = async (signedDocument?: File): Promise<boolean> => {
      try {
        loader.start("sendFormVersionLoader");
        const {
          selectedVersion,
          formSectionList,
          reportingPeriodFormType,
          isSignedReportRequired
        } = _store.getState<TFormDetailsPresenter>();
        let validationErrors = validateForm(formSectionList);

        let { firstValidatedAttribute } =
          _store.getState<TFormDetailsPresenter>();
        if (
          selectedVersion &&
          validationErrors &&
          validationErrors.length === 0
        ) {
          const formQualifierValidationErrors = await _application.container
            .resolve<ValidateFormInteractor>("validateForm")
            .execute(selectedVersion.id);
          if (formQualifierValidationErrors.length === 0) {
            if (isSignedReportRequired && !signedDocument) {
              _store.update({ signedReportValidationError: true });
              window.document
                .getElementById("signature-section")
                ?.scrollIntoView({
                  block: "start",
                  behavior: "smooth",
                  inline: "nearest"
                });
              loader.stop("sendFormVersionLoader");
              return false;
            }

            if (signedDocument) {
              await _application.container
                .resolve<UploadSignedReportInteractor>("uploadSignedReport")
                .execute(selectedVersion.id, signedDocument);
            }

            await _application.container
              .resolve<SendFormVersionInteractor>("sendFormVersion")
              .execute(selectedVersion.id);

            _application.container
              .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
              .execute(translate("form_details.success_message"));

            reportingPeriodFormType?.formReportingPeriodId &&
              (await getFormVersions(
                reportingPeriodFormType.formReportingPeriodId
              ));

            _store.update({ signedReportValidationError: false });
            loader.stop("sendFormVersionLoader");
            return true;
          } else {
            _application.container
              .resolve<ShowErrorMessageInteractor>("showErrorMessage")
              .execute(
                translate("form_details.qualifier_form_validation_error")
              );
            _store.update({
              formQualifierValidationErrors,
              validationErrors,
              firstValidatedAttribute: undefined
            });
          }
        } else {
          _application.container
            .resolve<ShowErrorMessageInteractor>("showErrorMessage")
            .execute(translate("form_details.missing_required_value_message"));
          firstValidatedAttribute = validationErrors[0];
          _store.update({ validationErrors, firstValidatedAttribute });
        }
        loader.stop("sendFormVersionLoader");
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
        loader.stop("sendFormVersionLoader");
      }
      return false;
    };

    const scrollToValidationError = () => {
      const { firstValidatedAttribute, formQualifierValidationErrors } =
        _store.getState<TFormDetailsPresenter>();
      const firstValidatedQualifier = formQualifierValidationErrors?.[0];

      let validateId: number | undefined;
      if (firstValidatedAttribute?.formSectionId) {
        validateId = firstValidatedAttribute.formSectionId;
      } else if (firstValidatedAttribute?.formAttributeId) {
        validateId = firstValidatedAttribute.formAttributeId;
      }

      if (validateId !== undefined && validateId !== null) {
        document.getElementById(validateId.toString())?.scrollIntoView({
          block: "center",
          behavior: "smooth",
          inline: "start"
        });
      } else if (
        firstValidatedQualifier !== undefined &&
        firstValidatedQualifier !== null
      ) {
        document
          .getElementById(firstValidatedQualifier.toString())
          ?.scrollIntoView({
            block: "center",
            behavior: "smooth",
            inline: "start"
          });
      }
    };

    const showSendFormVersionDialog = async () => {
      loader.start("showSendFormVersionDialogLoader");
      const { selectedVersion, formSectionList } =
        _store.getState<TFormDetailsPresenter>();
      let validationErrors = validateForm(formSectionList);

      if (validationErrors.length > 0) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute(translate("form_details.missing_required_value_message"));
        const firstValidatedAttribute = validationErrors[0];
        _store.update({ validationErrors, firstValidatedAttribute });
        loader.stop("showSendFormVersionDialogLoader");
        scrollToValidationError();
        return;
      }

      if (selectedVersion) {
        const formQualifierValidationErrors = await _application.container
          .resolve<ValidateFormInteractor>("validateForm")
          .execute(selectedVersion.id);

        if (formQualifierValidationErrors.length > 0) {
          _application.container
            .resolve<ShowErrorMessageInteractor>("showErrorMessage")
            .execute(translate("form_details.qualifier_form_validation_error"));
          _store.update({
            formQualifierValidationErrors,
            validationErrors,
            firstValidatedAttribute: undefined
          });
          loader.stop("showSendFormVersionDialogLoader");
          scrollToValidationError();
          return;
        }
      }

      loader.stop("showSendFormVersionDialogLoader");
      _store.update({ sendFormModalVisible: true });
    };

    const hideSendFormVersionDialog = () => {
      _store.update({ sendFormModalVisible: false });
    };

    const setRightOfEntry = (value: boolean) => {
      _store.update({ hasRightOfEntry: value });
    };

    const deleteDraftFormVersion = async () => {
      try {
        const { selectedVersion, reportingPeriodFormType } =
          _store.getState<TFormDetailsPresenter>();
        selectedVersion &&
          (await _application.container
            .resolve<DeleteDraftVersionFormInteractor>("deleteDraftVersionForm")
            .execute(selectedVersion.id));

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("form_details.success_message_delete_draft_version")
          );
        reportingPeriodFormType?.formReportingPeriodId &&
          (await getFormVersions(
            reportingPeriodFormType.formReportingPeriodId
          ));
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const onChangeComapreVersionsModal = (visible: boolean) => {
      if (!visible) {
        _store.update({
          compareVersionsSelected: []
        });
      } else {
        const selectedVersion =
          _store.getState<TFormDetailsPresenter>().selectedVersion;
        selectedVersion &&
          _store.update({
            compareVersionsSelected: [selectedVersion.id]
          });
      }
      _store.update({
        comparisonVersionsModalVisible: visible
      });
    };

    const onSelectCompareVersions = (formVersions: number[]) => {
      _store.update({
        compareVersionsSelected: formVersions
      });
    };

    const getComparisonFormVersionsDetails = async () => {
      const compareVersionsSelected =
        _store.getState<TFormDetailsPresenter>().compareVersionsSelected;
      loader.start("comparisonFormVersionsLoader");
      if (compareVersionsSelected?.length) {
        const firstComparisonFormVersion = await application.container
          .resolve<GetFormDetailsInteractor>("getFormDetails")
          .execute(compareVersionsSelected[0]);
        const secondComparisonFormVersion = await application.container
          .resolve<GetFormDetailsInteractor>("getFormDetails")
          .execute(compareVersionsSelected[1]);
        _store.update({
          firstComparisonFormVersion: firstComparisonFormVersion.map(
            FormSectionPresentationModel
          ),
          secondComparisonFormVersion: secondComparisonFormVersion.map(
            FormSectionPresentationModel
          ),
          comparisonFormVersionsVisible: true,
          comparisonVersionsModalVisible: false
        });
      }
      loader.stop("comparisonFormVersionsLoader");
    };

    const onChangeComparisonFormVersionsVisible = (visible: boolean) => {
      _store.update({
        comparisonFormVersionsVisible: false,
        compareVersionsSelected: []
      });
    };

    const showPDFReport = () => {
      _store.update({ pdfReportVisbile: true });
    };

    const generatePdfReport = async () => {
      const { selectedVersion } = _store.getState<IFormDetailsPresenter>();

      if (selectedVersion) {
        await getPDFReportData(selectedVersion.id);
        showPDFReport();
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadFormVersionList,
      setReportingPeriodFormType,
      onSelectVersion,
      getReferenceValues,
      onChangeFormVersion,
      onCancel,
      changeFormAttribute,
      addRow,
      sendFormVersion,
      isDraftFormVersion,
      deleteRow,
      showSendFormVersionDialog,
      hideSendFormVersionDialog,
      loadReportingPeriodId,
      setRightOfEntry,
      deleteDraftFormVersion,
      onChangeComapreVersionsModal,
      onSelectCompareVersions,
      getComparisonFormVersionsDetails,
      onChangeComparisonFormVersionsVisible,
      generatePdfReport,
      loadAllowedFileTypes,
      loadIsSignedReportRequired,
      resetSignedReportValidationError,
      loadFormType
    };
  },
  defaultState
);

export default FormDetailsPresenter;
